/**
 * Allowed ranges for design inputs — sliders kept inside the band (KAPL,
 * 11-Oct-2026: "set the values between that range only ... from the UI
 * itself"). The band comes from the server's parameter-range table; the
 * server enforces it too: a value outside needs a reason and becomes an
 * override the project's checker / approver approves.
 */
import React, { useState, useEffect, useCallback } from "react";

const KZ = { blue: "#1F4E79", grey: "#6B7280", red: "#DC2626", amber: "#B45309", green: "#047857" };

export function useRanges(api, authFetch) {
  const [rows, setRows] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const load = useCallback(() => authFetch(`${api}/api/eng/parameter-ranges`)
    .then(r => (r.ok ? r.json() : { rows: [] }))
    .then(d => { setRows(d.rows || []); setCanEdit(!!d.can_edit); })
    .catch(() => { }), [api, authFetch]);
  useEffect(() => { load(); }, [load]);
  return { rows, canEdit, reload: load, setRows };
}

/** "ISO-8", "iso 8", "ISO8" -> "ISO 8" (the table's class key). */
export function isoKey(cls) {
  const m = String(cls || "").toUpperCase().match(/ISO\s*-?\s*([1-9])/);
  return m ? `ISO ${m[1]}` : null;
}

const CLASS_KEYS = ["ach", "ach_max", "filter_coverage_pct", "filter_coverage_max_pct"];

/** The band for a key: its variant's (class / pattern), else the general one. */
export function bandFor(rows, key, scope, room) {
  let variant = null;
  if (room && CLASS_KEYS.includes(key)) {
    variant = isoKey(room.cleanroom_class);
    return variant ? rows.find(r => r.key === key && r.scope === scope && r.variant === variant) || null : null;
  }
  if (room && key === "hepa_face_velocity_ms") {
    variant = String(room.airflow_pattern || "").toUpperCase() === "UDAF" ? "UDAF" : "NON_UDAF";
  }
  return rows.find(r => r.key === key && r.scope === scope && r.variant === variant)
      || rows.find(r => r.key === key && r.scope === scope && !r.variant) || null;
}

const dp = step => (step && step < 1 ? Math.min(4, String(step).split(".")[1]?.length || 2) : 0);
export const outside = (v, b) => b && v !== "" && v !== null && v !== undefined
  && Number.isFinite(Number(v)) && (Number(v) < b.min - 1e-9 || Number(v) > b.max + 1e-9);

/** A slider inside the band plus a number box. `scale` converts the stored
 *  unit to the shown one (RH stored 0.55, shown 55). */
// `derived`: what a blank resolves to (e.g. air density from the site weather).
// Shown greyed in the box and on the slider; typing a value still overrides it.
export function RangeInput({ band, value, onChange, disabled, scale = 1, style, derived }) {
  if (!band) return null;
  const lo = band.min * scale, hi = band.max * scale, step = (band.step || 0.01) * scale;
  const num = value === "" || value === null || value === undefined ? null : Number(value);
  const off = num !== null && (num < lo - 1e-9 || num > hi + 1e-9);
  const shown = num !== null ? num : (derived === null || derived === undefined ? null : Number(derived));
  const pos = shown === null ? lo : Math.min(hi, Math.max(lo, shown));
  return (
    <div style={style}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input type="range" min={lo} max={hi} step={step} value={pos} disabled={disabled || lo === hi}
          onChange={e => onChange(String(+Number(e.target.value).toFixed(dp(step))))}
          style={{ flex: 1, accentColor: KZ.blue, minWidth: 60 }} />
        <input type="number" step="any" value={value ?? ""} disabled={disabled}
          placeholder={num === null && shown !== null ? String(shown) : undefined}
          title={num === null && shown !== null ? "Derived — type a value only to override" : undefined}
          onChange={e => onChange(e.target.value)}
          style={{ width: 74, padding: "5px 6px", fontSize: 12.5, borderRadius: 6,
                   border: `1.5px solid ${off ? KZ.red : "#E5E7EB"}`, background: off ? "#FEF2F2" : "#fff" }} />
      </div>
      <div title={band.source || ""} style={{ fontSize: 10.5, marginTop: 2, color: off ? KZ.red : "#9CA3AF" }}>
        {+(lo).toFixed(4)}–{+(hi).toFixed(4)}{band.unit && band.unit !== "fraction" && scale === 1 ? ` ${band.unit}` : ""}
        {" · "}<span style={{ color: band.status === "KAPL" ? KZ.green : KZ.amber, fontWeight: 600 }}>
          {band.status === "KAPL" ? "KAPL" : "CONFIRM"}</span>
        {off && " · outside — needs a reason and approval"}
      </div>
    </div>
  );
}

/** Save with the override flow: on OUT_OF_RANGE ask for the reason and send
 *  again with it. `send(reason)` returns the fetch Response. */
export async function saveWithReason(send) {
  let r = await send(null);
  let d = await r.json().catch(() => ({}));
  if (r.status === 409 && d.detail && d.detail.code === "OUT_OF_RANGE") {
    const reason = window.prompt(`${d.detail.message}\n\nReason for this value:`);
    if (!reason || !reason.trim()) throw new Error("Not saved — a value outside its range needs a reason.");
    r = await send(reason.trim());
    d = await r.json().catch(() => ({}));
  }
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : (d.detail?.message || "Could not save"));
  return d;
}

/** Every value of a project outside its band, with justify / approve. */
export function RangeOverrides({ api, authFetch, projectId, tick, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => authFetch(`${api}/api/eng/projects/${projectId}/range-overrides`)
    .then(r => r.json()).then(setD).catch(e => setErr(String(e.message || e))), [api, authFetch, projectId]);
  useEffect(() => { load(); }, [load, tick]);
  const post = async (path, body) => {
    setErr("");
    const r = await authFetch(`${api}/api/eng/projects/${projectId}/range-overrides${path}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(typeof j.detail === "string" ? j.detail : "Could not save"); return; }
    setD(j); onChanged && onChanged();
  };
  if (!d) return <div style={{ fontSize: 12.5, color: KZ.grey }}>{err || "Checking ranges…"}</div>;
  if (!d.rows.length) return <div style={{ fontSize: 12.5, color: KZ.green }}>Every ranged input is inside its allowed range.</div>;
  const tone = { APPROVED: KZ.green, PENDING: KZ.amber, REJECTED: KZ.red, UNJUSTIFIED: KZ.red };
  const th = { padding: "5px 8px", fontSize: 11, color: KZ.grey, borderBottom: "1px solid #E5E7EB" };
  const td = { padding: "5px 8px", fontSize: 12.5, borderBottom: "1px solid #F3F4F6", verticalAlign: "top" };
  return (
    <div>
      {err && <div style={{ color: KZ.red, fontSize: 12.5, marginBottom: 6 }}>{err}</div>}
      <div style={{ fontSize: 12.5, marginBottom: 6, color: d.unapproved.length ? KZ.red : KZ.green }}>
        {d.unapproved.length
          ? `${d.unapproved.length} value(s) outside their range are not approved — a run cannot be released until they are.`
          : "Every out-of-range value is approved."}</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Room", "Parameter", "Value", "Allowed", "State", "Reason / decision", ""].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{d.rows.map((x, i) => (
            <tr key={i}>
              <td style={{ ...td, fontWeight: 600 }}>{x.room || "Project design basis"}</td>
              <td style={td}>{x.label}{x.variant ? ` (${x.variant})` : ""}</td>
              <td style={{ ...td, fontWeight: 700 }}>{+x.value.toFixed(4)}</td>
              <td style={td} title={x.source}>{+x.min.toFixed(4)}–{+x.max.toFixed(4)} {x.unit && x.unit !== "fraction" ? x.unit : ""}</td>
              <td style={{ ...td, color: tone[x.state], fontWeight: 700 }}>{x.state}</td>
              <td style={{ ...td, maxWidth: 320, color: KZ.grey }}>
                {x.reason ? <>“{x.reason}” — {x.requested_by}</> : "no reason given"}
                {x.decided_by && <div>{x.state.toLowerCase()} by {x.decided_by}{x.decision_note ? `: ${x.decision_note}` : ""}</div>}</td>
              <td style={{ ...td, whiteSpace: "nowrap" }}>
                {(x.state === "UNJUSTIFIED" || x.state === "REJECTED") && (
                  <button onClick={() => { const r = window.prompt(`Reason for ${x.label} = ${x.value}:`); if (r && r.trim()) post("/justify", { room_id: x.room_id, key: x.key, reason: r.trim() }); }}
                    style={{ fontSize: 12, padding: "3px 8px", cursor: "pointer" }}>Give reason</button>)}
                {x.state === "PENDING" && x.override_id && <>
                  <button onClick={() => post(`/${x.override_id}/decide`, { approve: true, note: window.prompt("Approval note (optional):") || null })}
                    style={{ fontSize: 12, padding: "3px 8px", cursor: "pointer", marginRight: 4 }}>Approve</button>
                  <button onClick={() => { const n = window.prompt("Why is it rejected?"); if (n !== null) post(`/${x.override_id}/decide`, { approve: false, note: n }); }}
                    style={{ fontSize: 12, padding: "3px 8px", cursor: "pointer" }}>Reject</button></>}
              </td>
            </tr>))}</tbody>
        </table>
      </div>
      <div style={{ fontSize: 11.5, color: KZ.grey, marginTop: 6 }}>Approve / reject: the project's named checker or approver, never the person who set the value.</div>
    </div>
  );
}

/** Super Admin: the band of every ranged input. */
export function ParameterRangesAdmin({ api, authFetch }) {
  const { rows, canEdit, reload } = useRanges(api, authFetch);
  const [edit, setEdit] = useState({});
  const [err, setErr] = useState("");
  const save = async (r) => {
    setErr("");
    const e = edit[r.id] || {};
    const res = await authFetch(`${api}/api/eng/parameter-ranges/${r.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...(e.min !== undefined ? { min: Number(e.min) } : {}),
                             ...(e.max !== undefined ? { max: Number(e.max) } : {}),
                             ...(e.step !== undefined ? { step: Number(e.step) } : {}),
                             ...(e.status ? { status: e.status } : {}),
                             ...(e.source !== undefined ? { source: e.source } : {}) }) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) { setErr(typeof j.detail === "string" ? j.detail : "Could not save"); return; }
    setEdit(s => { const n = { ...s }; delete n[r.id]; return n; }); reload();
  };
  const th = { padding: "5px 8px", fontSize: 11, color: KZ.grey, borderBottom: "1px solid #E5E7EB", position: "sticky", top: 0, background: "#fff" };
  const td = { padding: "4px 8px", fontSize: 12.5, borderBottom: "1px solid #F3F4F6" };
  const box = (r, k, w = 70) => canEdit
    ? <input value={edit[r.id]?.[k] ?? r[k] ?? ""} onChange={e => setEdit(s => ({ ...s, [r.id]: { ...(s[r.id] || {}), [k]: e.target.value } }))}
        style={{ width: w, padding: "3px 5px", fontSize: 12.5, border: "1px solid #E5E7EB", borderRadius: 5 }} />
    : (r[k] ?? "—");
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: 16, marginTop: 16 }}>
      <div style={{ fontWeight: 800, color: KZ.blue, fontSize: 15 }}>Parameter ranges</div>
      <div style={{ fontSize: 12.5, color: KZ.grey, margin: "4px 0 10px" }}>
        The band each design input's slider is held inside. KAPL = stated in KAPL's documents (ACH and coverage by
        class from the Rev04 Class Reference); CONFIRM = provisional, for KAPL to confirm. A value outside its band
        needs a reason and the project checker / approver's approval. {canEdit ? "Super Admin: edit and save a row." : "Only a Super Admin edits these."}</div>
      {err && <div style={{ color: KZ.red, fontSize: 12.5 }}>{err}</div>}
      <div style={{ overflow: "auto", maxHeight: "60vh" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Scope", "Parameter", "Class / pattern", "Min", "Max", "Step", "Unit", "Status", "Source", ""].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id}>
              <td style={{ ...td, color: KZ.grey }}>{r.scope === "ROOM" ? "Room" : "Project"}</td>
              <td style={{ ...td, fontWeight: 600 }}>{r.label}</td>
              <td style={td}>{r.variant || "all"}</td>
              <td style={td}>{box(r, "min")}</td><td style={td}>{box(r, "max")}</td><td style={td}>{box(r, "step", 56)}</td>
              <td style={{ ...td, color: KZ.grey }}>{r.unit}</td>
              <td style={td}>{canEdit
                ? <select value={edit[r.id]?.status ?? r.status} onChange={e => setEdit(s => ({ ...s, [r.id]: { ...(s[r.id] || {}), status: e.target.value } }))}
                    style={{ fontSize: 12 }}><option>KAPL</option><option>CONFIRM</option></select>
                : <span style={{ color: r.status === "KAPL" ? KZ.green : KZ.amber, fontWeight: 600 }}>{r.status}</span>}</td>
              <td style={{ ...td, fontSize: 11.5, color: KZ.grey, maxWidth: 320 }}>{canEdit ? box(r, "source", 260) : r.source}</td>
              <td style={td}>{canEdit && edit[r.id] && <button onClick={() => save(r)} style={{ fontSize: 12, cursor: "pointer" }}>Save</button>}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}
